import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';

import type { User } from '@verantos-dev/core-annotation-tool';
import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import {
  computeHasChanges,
  editProjectReducer,
  initialEditProjectState,
  type EditProjectState,
  type ReallocationOperation,
} from './editProjectReducer';
import { DISABLE_EDIT_FOR_INACTIVE_PROJECT_FF } from '../constants';

interface EditProjectContextValue extends EditProjectState {
  projectId: string;
  hasChanges: boolean;
  /** True when entering/staying in edit mode is blocked (inactive project, flag on). */
  isEditBlocked: boolean;
  enterEditMode: () => void;
  exitEditMode: () => void;
  cancelEdits: () => void;
  addAnnotator: (person: User) => void;
  removeAnnotator: (annotatorId: string) => void;
  addSupervisor: (person: User) => void;
  removeSupervisor: (supervisorId: string) => void;
  commitReallocations: (operations: ReallocationOperation[]) => void;
  clearReallocations: () => void;
}

const noop = () => undefined;

/**
 * Default value so components (e.g. ProjectOverview) can render outside of an
 * EditProjectProvider in a read-only state — mirrors SupervisorModeContext.
 */
const defaultValue: EditProjectContextValue = {
  ...initialEditProjectState,
  projectId: '',
  hasChanges: false,
  isEditBlocked: false,
  enterEditMode: noop,
  exitEditMode: noop,
  cancelEdits: noop,
  addAnnotator: noop,
  removeAnnotator: noop,
  addSupervisor: noop,
  removeSupervisor: noop,
  commitReallocations: noop,
  clearReallocations: noop,
};

const EditProjectContext = createContext<EditProjectContextValue>(defaultValue);

export const useEditProject = (): EditProjectContextValue => useContext(EditProjectContext);

interface EditProjectProviderProps {
  projectId: string;
  /** Start in edit mode (e.g. when navigated to with ?edit=1). */
  initialEditMode?: boolean;
  /** Project's current status is `PROJECT_STATUS_INACTIVE` — may arrive after mount, once fetched. */
  isProjectInactive?: boolean;
  children: ReactNode;
}

export const EditProjectProvider = ({
  projectId,
  initialEditMode = false,
  isProjectInactive = false,
  children,
}: EditProjectProviderProps) => {
  // TODO: cleanup with at_disable_edit_option_for_inactive_proj ff
  const isEditDisableForInactiveEnabled = Boolean(
    useFeatureFlagSplit(DISABLE_EDIT_FOR_INACTIVE_PROJECT_FF)
  );
  const isEditBlocked = isEditDisableForInactiveEnabled && isProjectInactive;

  const [state, dispatch] = useReducer(editProjectReducer, {
    ...initialEditProjectState,
    editMode: initialEditMode && !isEditBlocked,
  });

  // `isProjectInactive` can arrive after mount (the project details fetch is still in
  // flight when a ?edit=1 deep link sets the initial state above) — force back out of
  // edit mode once it resolves to inactive under the flag.
  useEffect(() => {
    if (isEditBlocked && state.editMode) {
      dispatch({ type: 'EXIT_EDIT_MODE' });
    }
  }, [isEditBlocked, state.editMode]);

  const value = useMemo<EditProjectContextValue>(
    () => ({
      ...state,
      projectId,
      hasChanges: computeHasChanges(state),
      isEditBlocked,
      enterEditMode: () => dispatch({ type: 'ENTER_EDIT_MODE' }),
      exitEditMode: () => dispatch({ type: 'EXIT_EDIT_MODE' }),
      cancelEdits: () => dispatch({ type: 'CANCEL_EDITS' }),
      addAnnotator: (person) => dispatch({ type: 'ADD_ANNOTATOR', person }),
      removeAnnotator: (annotatorId) => dispatch({ type: 'REMOVE_ANNOTATOR', annotatorId }),
      addSupervisor: (person) => dispatch({ type: 'ADD_SUPERVISOR', person }),
      removeSupervisor: (supervisorId) => dispatch({ type: 'REMOVE_SUPERVISOR', supervisorId }),
      commitReallocations: (operations) => dispatch({ type: 'COMMIT_REALLOCATIONS', operations }),
      clearReallocations: () => dispatch({ type: 'CLEAR_REALLOCATIONS' }),
    }),
    [state, projectId, isEditBlocked]
  );

  return <EditProjectContext.Provider value={value}>{children}</EditProjectContext.Provider>;
};
